class ProductRecommendations extends HTMLElement {
  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) continue;

      this.#loadRecommendations();
      break;
    }
  });

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { recommendationsPerformed, sectionId, intent, context } = this.dataset;
    const id = this.id;

    if (!id) {
      this.#handleError(new Error('An ID attribute is required'));
      return;
    }

    if (recommendationsPerformed === 'true') return;

    // CART = multi-product merge
    if (context === 'cart') {
      this.#loadCartRecommendations(sectionId, intent).catch((e) => this.#handleError(e));
      return;
    }

    // PRODUCT (default) = original behavior
    const { productId } = this.dataset;
    if (!productId) {
      // On non-product pages you might intentionally not have productId.
      // In that case, do nothing and let Liquid fallback display.
      this.dataset.recommendationsPerformed = 'true';
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => this.#handleError(e));
  }

  /**
   * Fetches the recommendations and caches the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const baseUrl = this.dataset.url;
    if (!baseUrl) {
      return { success: false, status: 0 };
    }

    // Prevent "undefined" strings ending up in query
    const sid = sectionId ? `&section_id=${encodeURIComponent(sectionId)}` : '';
    const it  = intent ? `&intent=${encodeURIComponent(intent)}` : '';

    // If baseUrl already contains product_id, we still append a product_id; Shopify will take last.
    const url = `${baseUrl}&product_id=${encodeURIComponent(productId)}${sid}${it}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) return { success: true, data: cachedResponse };

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return { success: false, status: response.status };

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  // ================================
  // CART: multi-product recommendations
  // ================================
  async #loadCartRecommendations(sectionId, intent) {
    const id = this.id;

    const productIds = (this.dataset.productIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (!productIds.length) {
      // If cart empty, do nothing (Liquid fallback already rendered)
      this.dataset.recommendationsPerformed = 'true';
      return;
    }

    const excludeHandles = new Set(
      (this.dataset.excludeHandles || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    const wantedCollections = new Set(
      (this.dataset.collectionHandles || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    const limit = Number(this.dataset.limit || 12);

    // Fetch per cart item
    const all = [];
    for (const pid of productIds) {
      const result = await this.#fetchCachedRecommendations(pid, sectionId, intent);
      if (!result.success) continue;

      const parsed = this.#extractCardsFromResponse(result.data, id);
      all.push(...parsed);
    }

    const merged = this.#uniqByHandle(all).filter((p) => !excludeHandles.has(p.handle));

    merged.sort(
      (a, b) =>
        this.#scoreByCollections(b.collections, wantedCollections) -
        this.#scoreByCollections(a.collections, wantedCollections)
    );

    const picked = merged.slice(0, limit);

    const grid = this.querySelector('.glass-grid');
    if (!grid) {
      this.#handleError(new Error('Missing .glass-grid in section HTML'));
      return;
    }

    if (!picked.length) {
      // Don’t hide the whole section on cart; just mark performed.
      this.dataset.recommendationsPerformed = 'true';
      return;
    }

    grid.innerHTML = picked.map((p) => p.cardHTML).join('');
    this.dataset.recommendationsPerformed = 'true';
  }

  #extractCardsFromResponse(responseHtml, id) {
    const html = document.createElement('div');
    html.innerHTML = responseHtml || '';
    const recRoot = html.querySelector(`product-recommendations[id="${id}"]`);
    if (!recRoot) return [];

    const cards = [...recRoot.querySelectorAll('.glass-grid .product-item')];

    return cards
      .map((card) => {
        const a = card.querySelector('a.product-card-link');
        const href = a?.getAttribute('href') || '';
        const handle = href.split('/products/')[1]?.split(/[?#/]/)[0] || '';

        const collections = (card.getAttribute('data-collections') || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        return { handle, collections, cardHTML: card.outerHTML };
      })
      .filter((x) => x.handle);
  }

  #uniqByHandle(items) {
    const seen = new Set();
    const out = [];
    for (const it of items) {
      if (seen.has(it.handle)) continue;
      seen.add(it.handle);
      out.push(it);
    }
    return out;
  }

  #scoreByCollections(collections, wantedSet) {
    let score = 0;
    for (const c of collections) {
      if (wantedSet.has(c)) score += 10;
    }
    return score;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
